import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, tap, toArray, take, switchMap } from 'rxjs/operators';
import { AuthService } from '@core/auth.service';
import { LuckyNumberService } from 'src/app/services/lucky-number.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';
import { GTMSegmentService } from 'src/app/services/gtm-segment.service';
import { SyndicatePaymentService } from 'src/app/services/SyndicateService/syndicate-payment.service';
import { AuthGamesService } from '@core/AuthServices/authGames/auth-games.service';
import { Data } from '@angular/router';

@Injectable({
  providedIn: null
})
export class SyndicateUIHelperService {

  constructor(
    private authService: AuthService,
    private luckyNumberService: LuckyNumberService,
    private authGamesService: AuthGamesService,
    private syndicatePaymentService: SyndicatePaymentService,
    private gtmSegmentService: GTMSegmentService
  ) {}

  /**
   * Line chart creation (Chart.js)
   */
  lineChartMethod(lineCanvas: Data, currentStats: Data): Data {
    try {
      const chartLabel: string[] = [];
      const data = currentStats?.section?.[3]?.data;

      if (!Array.isArray(data)) {
        return {
          success: false,
          message: 'data is not iterable or is undefined'
        };
      }
      data.forEach(() => chartLabel.push(''));

      return {
        chart: new (window as Data).Chart(lineCanvas?.nativeElement?.getContext('2d'), {
          type: 'line',
          data: {
            labels: chartLabel,
            datasets: [
              {
                label: ' ',
                data,
                fill: true,
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.2,
                backgroundColor: 'rgb(213 226 235)',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                title: {
                  text: '• Checks',
                  display: true,
                  padding: -20,
                },
                labels: {
                  boxHeight: 0,
                  boxWidth: 0,
                  padding: -20,
                },
              },
            },
          },
        }),
        chartLabel
      };
    } catch (e) {
      return { error: e };
    }
  }

  /**
   * Pending status logic
   */
  pendingStatusIcon(syndicateLoaded: Data[], destroy$): void {
    if (!Array.isArray(syndicateLoaded)) return;
    syndicateLoaded.forEach((loaded: Data) => {
      try {
        const products = loaded?.products ?? [];

        from(products)
          .pipe(takeUntil(destroy$))
          .subscribe((product: Data) => {
            if (
              (loaded?.syndicate_type === 'One-off' && (!product?.checked || product?.checked === '')) ||
              (loaded?.syndicate_type === 'Ongoing' &&
                loaded?.active_status === 'ended' &&
                loaded?.next_draw_date &&
                loaded?.next_draw_date?.toDate().getTime() > 0 &&
                loaded?.next_draw_date?.toDate().toISOString()?.split('T')[0] >=
                  new Date().toISOString()?.split('T')[0])
            ) {
              loaded.pending = true;
            }
          });
      } catch {
        //handle error 
      }
    });
  }

  /**
   * Lucky number aggregation
   */
  updateLuckyNumberIcon(
    syndicateLoaded: Data[],
    destroy$,
    showMiniWidget: boolean,
    showExpandWidget: boolean,
    showAvailableWidget: boolean
  ): void {
    if (!(showMiniWidget || showExpandWidget || showAvailableWidget)) return;
    syndicateLoaded.forEach((loaded) => {
      const products = loaded?.products ?? [];
      let activeCount = 0;
      let appliedCount = 0;

      from(products).pipe(
        mergeMap((product: Data) => {
          const nextDraw = product?.next_draw_date?.toDate?.() ?? new Date();
          const now = new Date();

          if (product?.selected_numbers) appliedCount++;

          return this.luckyNumberService.getLotteryData(product?.product_name).pipe(
            tap((meta: Data) => {
              const drawCloseTime = meta?.draw_close_time ?? '11:59:00';
              const expired = this.luckyNumberService.isDrawDateExpired(nextDraw, now, drawCloseTime);
              if (!expired) activeCount++;
            }),
            map(() => null),
            catchError(() => of(null))
          );
        }, 5),
        toArray(),
        takeUntil(destroy$),
        catchError(() => of([]))
      ).subscribe(() => {
        loaded.lucky_number_applied_count = appliedCount;
        loaded.active_product_count = activeCount;
      });
    });
  }

  /**
   * Gold ring aggregation
   */
  updateGoldRingIcon(syndicateLoaded: Data[], destroy$, showGoldRingMiniWidget: boolean, showGoldExpandWidget: boolean, showGoldRingAvailableSyndicateWidget: boolean): void {
    if (!Array.isArray(syndicateLoaded)) return;
    if (!(showGoldRingMiniWidget || showGoldExpandWidget || showGoldRingAvailableSyndicateWidget)) {
      return;
    }

    syndicateLoaded.forEach((loaded) => {
      let count = 0;

      from(loaded?.products ?? [])
        .pipe(takeUntil(destroy$), catchError(() => of(null)))
        .subscribe((product: Data) => {
          if (product?.gold_ring) count++;
          loaded.gold_ring_applied_count = count;
        });
    });
  }

  /**
   * Mixpanel event
   */
  loadEvent(
    syndicatePageData$: Observable<Data>,
    mixpanelService: MixpanelService
  ): void {
    const start = new Date();
    const end = new Date();

    const dwellTime = this.formatDwellTime(end.getTime() - start.getTime());

    const journeyId = localStorage.getItem('datatoSyndi');
    const scrollDepth = localStorage.getItem('scrollDepthValue');

    syndicatePageData$.pipe(take(1)).subscribe((data) => {
      const event = data?.[0];
      console.warn("loadEvent", event);
      mixpanelService.GTMPopupInitializationForInboxNew('Ticket Popup Viewed', {
        Syndicate_Name: event?.syndicate_name,
        Draw_day: event?.products?.[0]?.draw_day,
        Line_Count: event?.total_lines,
        Scroll_Depth: scrollDepth + '%',
        Journey_ID: journeyId,
        Dwell_Time: dwellTime,
      });
    });

    localStorage.removeItem('datatoSyndi');
    localStorage.removeItem('scrollDepthValue');
  }

  /**
   * Time formatter
   */
  private formatDwellTime(ms: number): string {
    const sec = Math.floor((ms / 1000) % 60);
    const min = Math.floor((ms / (1000 * 60)) % 60);
    const hr = Math.floor((ms / (1000 * 60 * 60)) % 24);
    return hr > 0 ? `${hr}h ${min}m` : `${min}m ${sec}s`;
  }

  /**
   * Buy again logic
   */
  showBuyAgainUI(syndicate: Data): boolean {
    const endDate = syndicate?.syndicate_end_date?.trim();
    if (!endDate) return false;

    return (
      syndicate?.current_status === 'clearing' &&
      syndicate?.syndicate_type === 'One-off' &&
      syndicate?.offer_details?.offer_currency === 'cash' &&
      this.getTimeDiff(endDate)
    );
  }

  /**
   * Time diff logic
   */
  getTimeDiff(synEndDate: string): boolean {
    if (synEndDate === undefined || synEndDate === null || typeof synEndDate !== 'string') {
      return false;
    }

    const trimmedDate = synEndDate.trim();
    if (trimmedDate === '') {
      return false;
    }

    try {
      const dates = trimmedDate.replace("AM", "").replace("PM", "");
      const targetDate = new Date(dates);

      if (isNaN(targetDate.getTime())) {
        return false;
      }

      const currentTime = new Date();

      const timeDifference = currentTime.getTime() - targetDate.getTime();

      if (timeDifference > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Typeform integration
   */
  openTypeFormForFeedback(notification: Data, language: string): void {
    if (
      notification?.action?.type === 'nav' &&
      !notification?.viewed &&
      notification?.action?.details?.includes('typeform.com')
    ) {
      from(this.authService.getUid('AuthGamesService'))
        .pipe(
          switchMap((userId) => {
            if (!userId) return of(null);

            return this.authService
              .getAngularFireStore(`/users/${userId}/balance/current`, 'get')
              .pipe(map((res: Data) => ({ userId, balance: res?.data() })));
          })
        )
        .subscribe(({ userId, balance }: Data) => {
          if (!balance) return;

          const hiddenData = {
            user_id: userId,
            Point_balance: balance?.points,
            Credit_Balance: balance?.preferred_currency?.credit,
            Language: language || 'en',
            User_State: null,
          };

          this.authGamesService.typeForm(notification?.action?.details, hiddenData, notification);
        });
    }
  }

  /**
   * Offer click
   */
  clickOfferBanner(syndicate: Data, storeDiscountDetails: Data): void {
    this.authService
      ?.getOfferDetail(syndicate?.offer_details?.offer_id)
      ?.subscribe({
        next: (offer: Data) => {
          let data = Object.assign({
            line: offer?.lines - 1,
            real_name: offer?.lottery,
            prize: offer?.price,
            page: "store",
            productid: offer?.productid ? [...offer?.productid] : [],
            selectedDay: offer?.draw_day ? [...offer?.draw_day.map((m: string) => m?.trim())] : [],
            checkout: true,
            offers: offer,
            selectedDiscount: storeDiscountDetails,
            selectedLine: offer?.lines - 1,
          });

          const message = "quickPlay_clicked";
          this.gtmSegmentService?.GTMPopupInitialization(message, data);

          let queryParams = {
            TP1: "quickplay_sp",
          };

          data = { ...data, ...queryParams };
          this.syndicatePaymentService?.storePaymentPage(data);
        },
        error: (error) => {
          return error;
        },
      });
  }
}